import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getProviderAuthorityContext,
  getProviderDocument,
  getProviderEvidenceEvaluation,
  getProviderExtractedFacts,
  getProviderReviewHistory,
  routeProviderCandidates,
  runProviderAuthorityCheck,
  submitProviderReviewDecision,
} from '../../api/providerApi';
import { Button } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { NoticeBanner } from '../../components/ui/NoticeBanner';
import { StatusPill } from '../../components/ui/StatusPill';
import {
  REVIEW_DECISION_OPTIONS,
  authorityStateLabel,
  nextActionLabel,
  providerDecisionOutcome,
  recommendationLabel,
} from './providerReviewLabels';

function formatTimestamp(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function historyDecisionLabel(item) {
  if (item.is_authority_check) {
    return `Checklist ${item.decision}`;
  }
  return recommendationLabel(item.decision);
}

export function CandidateReviewDetailPage() {
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('task');
  const assetId = candidateId;

  const [document, setDocument] = useState(null);
  const [context, setContext] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [facts, setFacts] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [checkBusy, setCheckBusy] = useState(false);
  const [decision, setDecision] = useState('CANDIDATE');
  const [rationale, setRationale] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const loadDetail = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    setError('');
    try {
      const [documentResponse, contextResponse, evidenceResponse, factsResponse, historyResponse] =
        await Promise.all([
          getProviderDocument(assetId),
          getProviderAuthorityContext(assetId),
          getProviderEvidenceEvaluation(assetId),
          getProviderExtractedFacts(assetId),
          getProviderReviewHistory(assetId),
        ]);
      setDocument(documentResponse);
      setContext(contextResponse);
      setEvidence(evidenceResponse);
      setFacts(factsResponse);
      setHistory(historyResponse);
      if (
        contextResponse?.recommendation &&
        REVIEW_DECISION_OPTIONS.some((option) => option.value === contextResponse.recommendation)
      ) {
        setDecision(contextResponse.recommendation);
      }
    } catch (requestError) {
      setDocument(null);
      setContext(null);
      setEvidence(null);
      setFacts(null);
      setHistory(null);
      setError(requestError.message || 'Unable to load this Provider review.');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail, reloadToken]);

  const routed = document?.next_action === 'AWAITING_CUSTODIAN';
  const readyToRoute =
    document?.next_action === 'READY_TO_ROUTE' &&
    context?.recommendation === 'CANDIDATE' &&
    Boolean(taskId);
  const authorityState = document?.authority_state || context?.authority_state || 'PENDING';
  const outcome = useMemo(() => {
    if (routed) {
      return {
        title: 'Routed to Custodian',
        detail: `Awaiting Custodian legal authority. Authority state remains ${authorityState}.`,
      };
    }
    if (context?.recommendation) {
      return providerDecisionOutcome(context.recommendation, authorityState);
    }
    return null;
  }, [routed, context?.recommendation, authorityState]);

  const handleSaveDecision = async () => {
    if (!assetId || busy) return;
    setBusy(true);
    setActionError('');
    setNotice('');
    try {
      const result = await submitProviderReviewDecision(assetId, {
        decision,
        rationale: rationale.trim() || null,
        ai_recommendation: null,
        ai_recommendation_followed: false,
      });
      const recorded = providerDecisionOutcome(result.decision, result.authority_state);
      setNotice(`${recorded.title}. ${recorded.detail}`);
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to save the Provider decision.');
    } finally {
      setBusy(false);
    }
  };

  const handleRoute = async () => {
    if (!readyToRoute || busy) return;
    setBusy(true);
    setActionError('');
    setNotice('');
    try {
      await routeProviderCandidates([taskId]);
      setNotice('Routed to Custodian');
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to route this candidate.');
    } finally {
      setBusy(false);
    }
  };

  const handleAuthorityCheck = async () => {
    if (!assetId || checkBusy) return;
    setCheckBusy(true);
    setActionError('');
    try {
      const result = await runProviderAuthorityCheck(assetId, {
        notes: rationale.trim() || null,
      });
      setNotice(
        `Eligibility / authority checklist result: ${result.result}. This does not change legal authority.`
      );
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to run the eligibility checklist.');
    } finally {
      setCheckBusy(false);
    }
  };

  const evidenceItems = evidence?.items || [];
  const factItems = facts?.items || [];
  const historyItems = history?.items || [];

  return (
    <div className="space-y-5">
      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {notice ? <NoticeBanner>{notice}</NoticeBanner> : null}

      {loading && !document ? (
        <section className="rounded-2xl border border-eorig-border bg-white p-5 text-sm text-eorig-muted shadow-eorig-card">
          Loading candidate review…
        </section>
      ) : null}

      {document ? (
        <div className="grid gap-4 lg:grid-cols-[1fr_1.5fr] lg:items-start">
          <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-eorig-ink sm:text-lg">
                  Candidate {document.asset_number}
                </h2>
                <p className="mt-1 text-xs text-eorig-muted">
                  Loan {document.loan_number} · Package {document.batch} · Program {document.program}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusPill
                  value={document.next_action}
                  label={nextActionLabel(document.next_action)}
                />
                <StatusPill value={authorityState} label={authorityStateLabel(authorityState)} />
              </div>
            </div>
            <p className="mt-2 text-xs text-eorig-muted">
              Review task {taskId || 'not supplied'} · {nextActionLabel(document.next_action)} ·
              Authority {authorityStateLabel(authorityState)}
            </p>

            <h3 className="mt-5 text-sm font-semibold text-eorig-ink">
              Extracted facts
            </h3>
            {factItems.length > 0 ? (
              <ul className="mt-2 list-disc space-y-1.5 pl-4 text-sm text-eorig-ink">
                {factItems.map((fact) => (
                  <li key={fact.id}>
                    {fact.fact_key}: {fact.fact_value}
                    {fact.confidence != null ? ` (${fact.confidence})` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-eorig-muted">
                No extracted facts returned.
              </p>
            )}

            <h3 className="mt-6 text-sm font-semibold text-eorig-ink">
              Provider decision
            </h3>
            <fieldset className="mt-2 space-y-2 border-0 p-0" disabled={busy || routed}>
              <legend className="sr-only">Provider decision</legend>
              {REVIEW_DECISION_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 text-sm text-eorig-ink"
                >
                  <input
                    type="radio"
                    name="provider-decision"
                    value={option.value}
                    checked={decision === option.value}
                    onChange={() => setDecision(option.value)}
                    className="h-4 w-4 shrink-0 border-eorig-border text-eorig-navy-600 focus:ring-eorig-cyan/30"
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>

            <label className="mt-4 block text-sm text-eorig-ink">
              Rationale
              <textarea
                className="mt-1 w-full rounded-xl border border-eorig-border bg-white px-3 py-2 text-sm text-eorig-ink shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
                rows={3}
                value={rationale}
                onChange={(event) => setRationale(event.target.value)}
                disabled={busy || routed}
                aria-label="Decision rationale"
              />
            </label>

            {outcome ? (
              <p className="mt-4 text-sm text-eorig-ink" data-testid="provider-outcome">
                {outcome.title}. {outcome.detail}
              </p>
            ) : (
              <p className="mt-4 text-sm text-eorig-muted">
                No Provider recommendation yet. Review task starts unassigned with authority PENDING.
              </p>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="navy"
                onClick={handleSaveDecision}
                disabled={busy || routed}
              >
                {busy ? 'Saving…' : 'Save decision'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleRoute}
                disabled={!readyToRoute || busy}
              >
                Route to Custodian
              </Button>
            </div>
            {!taskId && document.next_action === 'READY_TO_ROUTE' ? (
              <p className="mt-2 text-xs text-eorig-muted">
                Open this candidate from the review queue to route it. The route API requires the
                review task id.
              </p>
            ) : null}
          </section>

          <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card sm:p-6">
            <h2 className="text-base font-semibold text-eorig-ink sm:text-lg">
              Source preview + evidence checklist
            </h2>
            <div className="mt-4 grid gap-4 lg:grid-cols-[1.2fr_1fr]">
              <div className="rounded-xl border border-eorig-border bg-eorig-surface p-3">
                <div className="flex aspect-[4/3] items-center justify-center rounded-lg border border-dashed border-eorig-border bg-white px-3 text-center text-xs text-eorig-muted">
                  {document.source_file_name
                    ? `Source file: ${document.source_file_name}. Preview bytes are not returned by the Provider document API.`
                    : 'No source file returned for this asset.'}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-eorig-ink">
                  Evidence checklist
                </h3>
                {evidenceItems.length > 0 ? (
                  <ul className="mt-2 list-disc space-y-1.5 pl-4 text-sm text-eorig-ink">
                    {evidenceItems.map((item) => (
                      <li key={item.id}>
                        {item.label} ({item.requirement_code}): {item.satisfied ? 'satisfied' : 'not satisfied'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-eorig-muted">
                    No evidence checklist items returned.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-eorig-border bg-eorig-surface px-3 py-2.5">
              <h3 className="text-sm font-semibold text-eorig-ink">
                Provider authority checklist
              </h3>
              <p className="mt-1 text-xs text-eorig-muted">
                Eligibility / authority checklist only. It records PASS / FAIL / WARN and does
                not change Asset.authority_state.
              </p>
              <p className="mt-2 text-xs text-eorig-ink">
                Evidence {context?.evidence_satisfied ?? 0}/{context?.evidence_total ?? 0}
                {context?.last_check_result ? ` · Last result ${context.last_check_result}` : ''}
              </p>
              <Button
                type="button"
                variant="ghost"
                className="mt-3"
                onClick={handleAuthorityCheck}
                disabled={checkBusy || routed}
              >
                {checkBusy ? 'Running…' : 'Run eligibility checklist'}
              </Button>
            </div>

            <div className="mt-5">
              <h3 className="text-sm font-semibold text-eorig-ink">
                Review history
              </h3>
              {historyItems.length > 0 ? (
                <ul className="mt-2 space-y-2 text-sm text-eorig-ink">
                  {historyItems.map((item) => (
                    <li key={item.id} className="rounded-xl border border-eorig-border px-3 py-2">
                      <p className="font-medium">{historyDecisionLabel(item)}</p>
                      <p className="text-xs text-eorig-muted">
                        {formatTimestamp(item.created_at)}
                        {item.rationale ? ` · ${item.rationale}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-eorig-muted">
                  No review history returned.
                </p>
              )}
            </div>
          </section>
        </div>
      ) : null}

      <Link
        to="/provider/authority-review"
        className="text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        ← Back to authority review queue
      </Link>
    </div>
  );
}
